import { useState, useEffect, useCallback } from 'react'
import {
  Square3Stack3DIcon,
  CpuChipIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { QueueMetrics as QueueMetricsType } from '../types/job'
import { getQueueMetrics } from '../services/api'
import { StatCard } from './ui'
import { errorText } from '../utils/errorText'

interface QueueMetricsProps {
  refreshInterval?: number // Default: 30 seconds
  /** Bump to refetch now. The page's Refresh button reloads the jobs and the queue together;
   *  without this the numbers above the list would keep showing the previous poll. */
  reloadKey?: number
}

/**
 * Queue Health — the reference's block above the jobs list (sap-mockup/app/(console)/jobs/page.tsx):
 * a small heading with the fetch time beside it, then six StatCards reading number, what it
 * counts, and the window it counts over.
 *
 * The stuck-job warning is ours; the reference has no such state. It stays because an operator
 * whose queue depth is climbing needs to be told the jobs at the front are wedged, not just that
 * there are many.
 */
export default function QueueMetrics({ refreshInterval = 30000, reloadKey = 0 }: QueueMetricsProps) {
  const [metrics, setMetrics] = useState<QueueMetricsType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const loadMetrics = useCallback(async () => {
    try {
      const data = await getQueueMetrics()
      setMetrics(data)
      setLastUpdated(new Date())
      setError(null)
    } catch (err: any) {
      setError(errorText(err, 'Failed to load metrics'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMetrics()
    const intervalId = setInterval(loadMetrics, refreshInterval)
    return () => clearInterval(intervalId)
  }, [loadMetrics, refreshInterval, reloadKey])

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h`
  }

  const heading = (
    <div className="mb-2 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-ftext">Queue Health</h3>
      {lastUpdated && (
        <span className="text-xs text-ffaint">Last updated: {lastUpdated.toLocaleTimeString()}</span>
      )}
    </div>
  )

  if (loading && !metrics) {
    return (
      <div className="mb-6">
        {heading}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="card h-[92px] animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error && !metrics) {
    return (
      <div className="mb-6">
        {heading}
        <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
          {error}
          <button onClick={loadMetrics} className="btn-ghost btn-sm ml-2">
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!metrics) return null

  return (
    <div className="mb-6">
      {heading}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Waiting jobs"
          value={metrics.queue_depth}
          icon={Square3Stack3DIcon}
          tone="amber"
          sub="Queue depth"
        />
        <StatCard
          label="Active"
          value={metrics.active_workers}
          icon={CpuChipIcon}
          tone="blue"
          sub="Workers"
        />
        <StatCard
          label="In progress"
          value={metrics.processing_jobs}
          icon={ArrowPathIcon}
          tone="violet"
          sub="Processing"
        />
        <StatCard
          label="Today"
          value={metrics.completed_today}
          icon={CheckCircleIcon}
          tone="green"
          sub="Completed"
        />
        <StatCard
          label="Today"
          value={metrics.failed_today}
          icon={XCircleIcon}
          tone="red"
          sub="Failed"
        />
        <StatCard
          label="Per job"
          value={formatTime(metrics.avg_processing_time_seconds)}
          icon={ClockIcon}
          tone="slate"
          sub="Avg time"
        />
      </div>

      {metrics.stuck_jobs.length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-300">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
            {metrics.stuck_jobs.length} stuck job{metrics.stuck_jobs.length > 1 ? 's' : ''} detected
          </p>
          <p className="mt-1 text-[12px] text-fmuted">
            These jobs have exceeded their timeout. Consider cancelling and retrying.
          </p>
        </div>
      )}
    </div>
  )
}
